import React, { useCallback, useMemo, useState } from 'react';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { meetingSessionToActivity } from 'util/meeting';

export const displayTrademarkStatement = 'display-trademark';

const TrademarkSection: React.FunctionComponent = () => {
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [hasTrademark, setHasTrademark] = useState<QuestionResponse | undefined>(
    meetingSession?.currentIpCheckSession?.hasTrademark
  );
  const [isJustified, setIsJustified] = useState<QuestionResponse | undefined>(
    meetingSession?.currentIpCheckSession?.isTrademarkJustified
  );

  const enableSave = useMemo(() => hasTrademark === QuestionResponse.No || isJustified, [hasTrademark, isJustified]);

  const saveTrademarkProgress = useCallback(
    async (patch: {
      displayTrademark?: boolean;
      statement?: string;
      hasTrademark?: QuestionResponse;
      isTrademarkJustified?: QuestionResponse;
    }) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: {
            ...meetingSession?.currentIpCheckSession,
            type: IPCheckType.Affirmative,
            copyrightConflict: undefined,
            copyrightSummary: undefined,
            ...patch,
          },
          currentTechnologyConflict: undefined,
        });
      } catch (error) {
        console.error('Error saving trademark progress:', error);
      }
    },
    [meetingId, meetingSession]
  );

  const handleDisplay = useCallback(async () => {
    try {
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentIpCheckSession: {
          ...meetingSession?.currentIpCheckSession,
          type: IPCheckType.Affirmative,
          displayTrademark: true,
          statement: displayTrademarkStatement,
          hasTrademark,
          isTrademarkJustified: isJustified,
          copyrightConflict: undefined,
          copyrightSummary: undefined,
        },
        currentTechnologyConflict: undefined,
      });
    } catch (error) {
      console.error('Error displaying trademark assessment:', error);
    }
  }, [hasTrademark, isJustified, meetingId, meetingSession]);

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        motion: meetingSession?.currentMotion,
        votingSession: meetingSession?.currentVotingSession,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          hasTrademark,
          isTrademarkJustified: isJustified,
        },
      },
      'Saved trademark assessment'
    );
  }, [hasTrademark, isJustified, logActivity, meetingSession]);

  const handleClear = useCallback(() => {
    setHasTrademark(undefined);
    setIsJustified(undefined);

    void saveTrademarkProgress({
      displayTrademark: undefined,
      statement: undefined,
      hasTrademark: undefined,
      isTrademarkJustified: undefined,
    });
  }, [saveTrademarkProgress]);

  return (
    <GridContainer spacing={1}>
      <GridItem>
        <Typography variant="h6">Trademark</Typography>
      </GridItem>
      <GridItem>
        <Typography>
          <strong>Assessment of disclosed (identified) trademark</strong>
        </Typography>
      </GridItem>
      <GridItem>
        <FormControl>
          <FormLabel>Is there any trademark in the Standard/Safety Guideline?</FormLabel>
          <RadioGroup
            name="has-trademark-radio-group"
            value={hasTrademark ?? ''}
            onChange={(event) => {
              const value = event.target.value as QuestionResponse;
              if (value === hasTrademark) return;

              const nextJustified = value === QuestionResponse.Yes ? isJustified : undefined;
              setHasTrademark(value);
              if (value !== QuestionResponse.Yes) {
                setIsJustified(undefined);
              }
              void saveTrademarkProgress({
                hasTrademark: value,
                isTrademarkJustified: nextJustified,
              });
            }}
          >
            <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
            <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
          </RadioGroup>
        </FormControl>
      </GridItem>
      {hasTrademark === QuestionResponse.Yes && (
        <>
          <GridItem>
            <FormControl>
              <FormLabel>Is every instance of trademark use technically justified?</FormLabel>
              <RadioGroup
                name="justified-use-radio-group"
                value={isJustified ?? ''}
                onChange={(event) => {
                  const value = event.target.value as QuestionResponse;
                  if (value === isJustified) return;

                  setIsJustified(value);
                  void saveTrademarkProgress({
                    hasTrademark: QuestionResponse.Yes,
                    isTrademarkJustified: value,
                  });
                }}
              >
                <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
                <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
              </RadioGroup>
            </FormControl>
          </GridItem>
          {isJustified === QuestionResponse.No && (
            <GridItem>
              <Typography>The Document is failed and returned to the TF</Typography>
            </GridItem>
          )}
        </>
      )}
      {(hasTrademark === QuestionResponse.No || isJustified === QuestionResponse.Yes) && (
        <GridItem>
          <Typography>Go to IP check completion condition check</Typography>
        </GridItem>
      )}
      <GridItem>
        <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={handleDisplay}>
          Display
        </Button>
        <Button variant="contained" color="secondary" disabled={!enableSave} onClick={handleSave}>
          Save
        </Button>
        <Button variant="contained" color="error" sx={{ ml: 1 }} onClick={handleClear}>
          Clear
        </Button>
      </GridItem>
    </GridContainer>
  );
};

export default TrademarkSection;
